import { createContext, type PropsWithChildren, useContext, useMemo, useState } from 'react';
import '../features/quiz-content/consts/events.quiz-detail-answer-visibility';
import { buildToggleChange, trackEvent } from '@zep/logger/tracking';
import { useExternalPlatform } from '@/shared/externalPlatform';
type AnswerVisibilitySource = 'global_toggle' | 'first_card_notice';
interface AnswerVisibility {
  isAnswerHidden: boolean;
  /** 정답 표시/숨김을 바꾸고 전환 이벤트를 보낸다 */
  changeAnswerVisibility: (isVisible: boolean, source: AnswerVisibilitySource) => void;
  /** 정답 보기 토글 노출 이벤트를 보낸다 */
  trackToggleView: () => void;
}
const AnswerVisibilityContext = createContext<AnswerVisibility | null>(null);
interface AnswerVisibilityProviderProps {
  quizId: string;
  quizCount: number;
  /** 잠긴 내 퀴즈(owner-locked) 포함 */
  isQuizAuthor: boolean;
}

/**
 * 상세페이지 정답 숨김/표시 상태와 그 동작·이벤트. 문제 목록(토글·카드)과 CTA(플레이 입장)가 함께 쓴다.
 * 상세페이지끼리 이동하는 동안은 퀴즈별로 정답 보기 상태를 기억한다(돌아오면 켜 둔 그대로).
 * 기록이 없는 퀴즈는 숨김으로 시작하고, 저장소에는 쓰지 않아 새로고침·상세 이탈 시 모두 숨김으로 돌아간다.
 */
export const AnswerVisibilityProvider = ({
  quizId,
  quizCount,
  isQuizAuthor,
  children
}: PropsWithChildren<AnswerVisibilityProviderProps>) => {
  const {
    mode: platformMode
  } = useExternalPlatform();
  const [visibleByQuizId, setVisibleByQuizId] = useState<Record<string, boolean>>({});
  const isAnswerHidden = !visibleByQuizId[quizId];
  const value = useMemo<AnswerVisibility>(() => ({
    isAnswerHidden,
    changeAnswerVisibility: (isVisible, source) => {
      setVisibleByQuizId(prev => ({
        ...prev,
        [quizId]: isVisible
      }));
      trackEvent('quiz_detail_answer_visibility_toggle', {
        ...buildToggleChange(isVisible),
        source,
        quiz_set_id: quizId,
        is_owner: isQuizAuthor,
        platform_mode: platformMode
      });
    },
    trackToggleView: () => {
      trackEvent('quiz_detail_answer_toggle_view', {
        quiz_set_id: quizId,
        quiz_count: quizCount,
        is_owner: isQuizAuthor,
        platform_mode: platformMode
      });
    }
  }), [isAnswerHidden, quizId, quizCount, isQuizAuthor, platformMode]);
  return <AnswerVisibilityContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="AnswerVisibilityProvider" data-sentry-source-file="AnswerVisibilityProvider.tsx">
      {children}
    </AnswerVisibilityContext.Provider>;
};
export const useAnswerVisibility = (): AnswerVisibility => {
  const context = useContext(AnswerVisibilityContext);
  if (!context) {
    throw new Error('useAnswerVisibility must be used within AnswerVisibilityProvider');
  }
  return context;
};

/** Provider 밖(플래시카드 등)에서도 쓰는 소비자용 — 없으면 null */
export const useOptionalAnswerVisibility = () => useContext(AnswerVisibilityContext);